import { Controller } from "@hotwired/stimulus"
import { usesSidebarLayout } from "../utils/layout_media"

export const JANE_SIDEBAR_OPEN_KEY = "jane_sidebar_open"

// Desktop Jane sidebar on home, messages, network, events, and tasks (server-rendered via
// shared/_jane_sidebar). Close hides the panel and persists preference;
// detail-modal / event-detail temporarily remove it while showing row detail,
// then dispatch restore (respecting saved preference).
// It can also host a live conversation ("Task Jane" from a task row dispatches
// jane-sidebar:conversation): the assistant detail panel is fetched into the
// content area, its kebab menu is mounted next to the close button, and
// compose submits stay in the sidebar instead of navigating to the chat page.
export default class extends Controller {
    // conversationId / personaId / purpose describe the thread the SERVER
    // already rendered into this panel (multi-persona M3). Their presence is
    // what tells this controller it is hosting a persistent thread rather than
    // sitting idle, which changes what a compose submit and "New conversation"
    // have to do — see _serverHosted below.
    static values = { section: String, conversationId: Number, personaId: Number, purpose: String }

    connect() {
        this._onRestore = () => this._restoreIfNeeded()
        this._onToggle = () => this.toggle()
        this._onResize = () => this._syncLayoutMode()
        this._onConversation = (e) => this._openConversation(e.detail?.conversationId)
        this._onSubmit = (e) => this._interceptComposeSubmit(e)
        this._onSent = (e) => this._composeSent(e.detail?.conversationId)
        document.addEventListener("jane-sidebar:restore", this._onRestore)
        document.addEventListener("jane-sidebar:toggle", this._onToggle)
        document.addEventListener("jane-sidebar:conversation", this._onConversation)
        window.addEventListener("resize", this._onResize)
        this.element.addEventListener("submit", this._onSubmit)
        this.element.addEventListener("jane-sidebar:sent", this._onSent)

        // A server-rendered thread: the panel's markup (persona face, thread
        // tabs, assistant-poll) is already correct, so a send must NOT swap it
        // for the generic detail panel — that would replace the persona header
        // with "Jane / Conversation" the moment you typed.
        if (this.hasConversationIdValue && this.conversationIdValue) {
            this._conversationId = this.conversationIdValue
            this._serverHosted = true
        }

        this._applyLayoutState()
    }

    disconnect() {
        document.removeEventListener("jane-sidebar:restore", this._onRestore)
        document.removeEventListener("jane-sidebar:toggle", this._onToggle)
        document.removeEventListener("jane-sidebar:conversation", this._onConversation)
        window.removeEventListener("resize", this._onResize)
        this.element.removeEventListener("submit", this._onSubmit)
        this.element.removeEventListener("jane-sidebar:sent", this._onSent)
    }

    // --- Conversation hosting (Task Jane) ---

    _openConversation(conversationId) {
        if (!conversationId) return
        this._conversationId = conversationId
        // Task Jane takes the panel over with its own fetched markup, so the
        // server-rendered thread (and its persona header) is gone from here on.
        this._serverHosted = false
        this._show()
        this._loadConversation()
    }

    _loadConversation() {
        const content = this.element.querySelector(".detail-sidebar-content")
        if (!content || !this._conversationId) return
        const url = `/assistant/detail_panel?conversation_id=${encodeURIComponent(this._conversationId)}&section=${encodeURIComponent(this.sectionValue || "tasks")}`
        const token = (this._loadToken = (this._loadToken || 0) + 1)
        fetch(url, { headers: { "Accept": "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => {
                if (token !== this._loadToken) return
                this._unmountActionsMenu()
                content.innerHTML = html
                this._mountActionsMenu(content)
                // A long thread opens on its newest message, where the answer to what was just asked is.
                requestAnimationFrame(() => {
                    const scroller = content.querySelector(".msg-detail-scroll") || content
                    scroller.scrollTop = scroller.scrollHeight
                })
            })
            .catch(() => {})
    }

    // The panel's kebab (open in new tab, …) is moved into the persistent
    // actions bar, left of the close button. Unmount-before-mount keeps it
    // idempotent across repeated opens (see msg_detail_controller history).
    _mountActionsMenu(content) {
        let menu = content.querySelector(".msg-detail-menu")
        if (!menu) return
        let actions = this.element.querySelector(".detail-sidebar-actions")
        let closeBtn = actions?.querySelector(".detail-sidebar-close")
        if (!actions || !closeBtn) return
        this._unmountActionsMenu()
        const configure = actions.querySelector('.jane-sidebar-configure')
        const panel = menu.querySelector('.assistant-header-menu-panel')
        if (configure && panel && !panel.querySelector('.jane-sidebar-configure')) panel.prepend(configure.cloneNode(true))
        actions.insertBefore(menu, actions.querySelector('.sidebar-view-menu') || closeBtn)
        this._toggleStaticMenu(false)
    }

    _unmountActionsMenu() {
        this.element.querySelectorAll(".detail-sidebar-actions .msg-detail-menu").forEach((el) => el.remove())
    }

    // Every send inside the sidebar posts via fetch and hosts the conversation
    // in place, so the user never leaves the page: the composer, the suggested
    // reply and starter chips and the tool-card buttons (all of which post
    // /assistant/message and are answered with a redirect to /messages/:id on
    // the HTML path), and the cards in the thread whose forms end in that same
    // redirect (ask cards, background-task Stop/Approve, the bulk email
    // confirm). Fresh sends (no conversation yet) get the new conversation id
    // back from the JSON response; the hosted panel's assistant-poll streams
    // the reply in.
    _interceptComposeSubmit(e) {
        const form = e.target.closest("form")
        if (!form || !this.element.contains(form)) return
        // assistant-compose already sent it (with any staged attachments) and
        // will report back via jane-sidebar:sent; sending again would double up.
        if (e.defaultPrevented) return
        if (form.matches("form.assistant-compose") || this._isMessageForm(form)) {
            this._sendMessage(e, form)
        } else if (this._isChatCardForm(form)) {
            this._postInPlace(e, form)
        }
    }

    _isMessageForm(form) {
        return new URL(form.action, location.href).pathname === "/assistant/message"
    }

    // A POST form drawn inside the thread that breaks out of its frame (data-
    // turbo-frame="_top", or Turbo off) and so ends in a full-page redirect.
    // The menu's own forms (New conversation, Delete conversation) sit outside
    // .assistant-chat and keep their redirect on purpose; forms that stay in
    // their frame are Turbo's to answer.
    _isChatCardForm(form) {
        if ((form.method || "").toLowerCase() !== "post" || !form.closest(".assistant-chat")) return false
        return form.dataset.turboFrame === "_top" || form.dataset.turbo === "false"
    }

    _sendMessage(e, form) {
        e.preventDefault()
        const fd = new FormData(form)
        if (!String(fd.get("message") || "").trim()) return
        // A project switch (new_project_chat) must start a NEW thread, so it
        // never inherits the one the pane is showing.
        if (this._conversationId && !fd.get("conversation_id") && fd.get("new_project_chat") !== "1") fd.append("conversation_id", this._conversationId)
        const field = form.querySelector(".assistant-compose-field")
        if (field) field.value = ""
        let switched = false
        fetch(form.action, {
            method: "POST",
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                "Accept": "application/json"
            },
            body: fd,
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (data?.conversation_id) switched = this._followConversation(data.conversation_id)
            })
            .catch(() => {})
            .finally(() => {
                if (switched) return
                // Server-rendered thread: the panel is already the right shape,
                // so just wake its poller and let the reply stream into the
                // existing chat frame. Refetching the detail panel here would
                // throw away the persona header and the thread tabs.
                if (this._serverHosted) this._restartPoll()
                else this._loadConversation()
            })
    }

    // A card's form that would redirect to the full chat page: post it in the
    // background (redirect: manual, so the redirect is never followed) and wake
    // the poller, which shows whatever the action did to the thread.
    _postInPlace(e, form) {
        e.preventDefault()
        const confirmText = form.dataset.turboConfirm
        if (confirmText && !window.confirm(confirmText)) return
        const fd = new FormData(form)
        form.querySelectorAll("button[type=submit]").forEach((button) => { button.disabled = true })
        fetch(form.action, {
            method: "POST",
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                "Accept": "text/vnd.turbo-stream.html, text/html"
            },
            body: fd,
            credentials: "same-origin",
            redirect: "manual"
        })
            .then((r) => {
                // An action that answers with a turbo stream (not a redirect)
                // still gets to update the page the way Turbo would have.
                if ((r.headers.get("Content-Type") || "").includes("turbo-stream") && window.Turbo?.renderStreamMessage) {
                    return r.text().then((html) => window.Turbo.renderStreamMessage(html))
                }
            })
            // Stays disabled on success (the poll redraws the card); a failed
            // post gives the button back so it can be tried again.
            .catch(() => form.querySelectorAll("button[type=submit]").forEach((button) => { button.disabled = false }))
            .finally(() => {
                if (this._serverHosted || this._conversationId) this._restartPoll()
            })
    }

    // The composer sent the message itself; stay on this page and follow the
    // thread it landed in.
    _composeSent(conversationId) {
        if (conversationId && this._followConversation(conversationId)) return
        if (this._serverHosted) this._restartPoll()
        else this._loadConversation()
    }

    // A send answers with the thread it landed in. Usually that is the one the
    // pane already shows, but a project switch starts a new one: host that
    // instead of polling a thread the user has left.
    _followConversation(conversationId) {
        const current = this._conversationId || (this.hasConversationIdValue ? this.conversationIdValue : 0)
        if (current && Number(conversationId) !== Number(current)) {
            this._openConversation(conversationId)
            return true
        }
        this._conversationId = conversationId
        return false
    }

    _restartPoll() {
        const panel = this.element.querySelector("[data-controller~='assistant-poll']")
        if (!panel) return
        const poll = this.application.getControllerForElementAndIdentifier(panel, "assistant-poll")
        poll?.restart()
    }

    // Kebab action: start a genuinely NEW thread.
    //
    // Threads persist now, so this cannot be the client-side reset it used to
    // be — clearing the id locally would just make the next message resume the
    // very thread the user asked to leave. It POSTs, and the server hands back
    // the fresh conversation (reusing an untouched one rather than stacking up
    // empties — see Assistant::ThreadResolver.start_new).
    newConversation(event) {
        event?.preventDefault()
        const body = new FormData()
        if (this.hasPersonaIdValue && this.personaIdValue) body.append("persona_id", this.personaIdValue)
        body.append("purpose", this.purposeValue || "chat")
        fetch("/assistant/new_conversation", {
            method: "POST",
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                "Accept": "application/json"
            },
            body,
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data?.conversation_id) return
                // The server-rendered panel resolves its thread on the way in,
                // and the new one is by definition the newest — a reload lands
                // in it with the persona header and tabs intact.
                if (this._serverHosted) {
                    window.location.reload()
                    return
                }
                this._openConversation(data.conversation_id)
            })
            .catch(() => {})
    }

    // The static kebab (idle sidebar) yields to the hosted panel's kebab,
    // which carries the conversation-specific actions.
    _toggleStaticMenu(show) {
        const menu = this.element.querySelector(".jane-sidebar-static-menu")
        if (menu) menu.style.display = show ? "" : "none"
    }

    // Where the panel sits is assistant-poll's job, not this controller's: the
    // HTML injected above carries data-controller="assistant-poll", which
    // connects on the swap and applies the chat policy (pin the sent message to
    // the top while a reply is in flight, otherwise land at the end — see
    // utils/chat_scroll). A bare scrollTop here raced that and always lost or
    // won by accident depending on frame timing.

    close(event) {
        event?.preventDefault()
        this._hide()
    }

    // The same window event the composer's mic raises in call mode
    // (assistant_compose#onClickButton). One call bar answers it for the whole
    // app (shared/_jane_call_dock), so there is only ever one call.
    call(event) {
        event?.preventDefault()
        window.dispatchEvent(new CustomEvent("sidebar:closed", { detail: { sidebar: this.element } }))
        const conversationId = this.hasConversationIdValue && this.conversationIdValue ? String(this.conversationIdValue) : null
        window.dispatchEvent(new CustomEvent("assistant-voice:open", { detail: { conversationId } }))
    }

    toggle() {
        if (!this._useSidebar()) return
        if (this._hidden) this._show()
        else this._hide()
    }

    hideForDetail() {
        if (!this.element.isConnected) return
        this._hide(true)
    }

    _restoreIfNeeded() {
        if (!this._useSidebar()) return
        if (!this._hidden) return
        if (!this._prefersOpen()) return
        this._show(true)
    }

    _prefersOpen() {
        // Home never auto-expands: its bottom composer is the primary input, and
        // opening the sidebar hides that composer (CSS) so two composers never
        // post to the same conversation. Opening on home is a per-visit action
        // (not persisted — see _persistOpen). Section sidebars (messages,
        // network, events, and tasks) remember the user's choice and open by default.
        if (this.sectionValue === "home") return false
        try {
            return localStorage.getItem(JANE_SIDEBAR_OPEN_KEY) !== "false"
        } catch {
            return true
        }
    }

    _persistOpen(open) {
        // Home's open state is per-visit, so it never writes the shared key (nor
        // reads it — see _prefersOpen); keeping home out avoids it also flipping
        // the section-sidebar default.
        if (this.sectionValue !== "home") {
            try {
                localStorage.setItem(JANE_SIDEBAR_OPEN_KEY, open ? "true" : "false")
            } catch {
                // ignore storage errors
            }
        }
        this._syncCollapsedClass(open)
    }

    _hide(skipSave = false) {
        window.dispatchEvent(new CustomEvent("sidebar:closed", { detail: { sidebar: this.element } }))
        this._hidden = true
        this.element.style.display = "none"
        document.querySelector(".assistant-phone")?.classList.remove("is-detail-open")
        this._syncCollapsedClass(false)
        if (!skipSave) this._persistOpen(false)
        this._syncToggleButtons(false)
    }

    _show(skipSave = false) {
        this._hidden = false
        this.element.style.display = ""
        if (this._useSidebar()) {
            document.querySelector(".assistant-phone")?.classList.add("is-detail-open")
        }
        this._syncCollapsedClass(true)
        if (!skipSave) this._persistOpen(true)
        this._syncToggleButtons(true)
    }

    _syncCollapsedClass(open) {
        document.documentElement.classList.toggle("jane-sidebar-user-collapsed", !open)
    }

    _syncToggleButtons(open) {
        document.querySelectorAll(".jane-sidebar-toggle").forEach((btn) => {
            const header = btn.closest(".header-assistant")
            const matching = !header || Number(header.dataset.janeSidebarTogglePersonaIdValue || 0) === this.personaIdValue
            btn.classList.toggle("is-active", open && matching)
            btn.setAttribute("aria-pressed", open && matching ? "true" : "false")
        })
    }

    // Open/closed only means anything in the split layout — narrower than that,
    // CSS hides the panel outright (.detail-sidebar { display: none }). So a
    // resize must NOT touch the state: it only settles it the first time the
    // viewport is wide enough, for a page that loaded too narrow to decide.
    // Clearing _hidden on the way DOWN (what this used to do) meant every
    // narrow-and-back-again drag re-opened the panel, ignoring both the saved
    // preference and home's closed-by-default rule.
    _syncLayoutMode() {
        if (this._stateApplied) return
        this._applyLayoutState()
    }

    _applyLayoutState() {
        if (!this._useSidebar()) return
        this._stateApplied = true
        if (this._prefersOpen()) this._show(true)
        else this._hide(true)
    }

    _useSidebar() {
        return usesSidebarLayout()
    }
}
